import { PublicSite, ReleaseManifest, Id } from '@ka/contracts';

export type LoadResult =
  | { kind: 'release'; manifest: ReleaseManifest; siteRevision: number }
  | { kind: 'empty' }
  | { kind: 'error'; message: string };

async function getJson(url: string): Promise<unknown> {
  const res = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
  if (!res.ok) throw new Error(`${url} -> ${res.status}`);
  return res.json();
}

/**
 * Resolve the release for this page session. The id is pinned for the whole session so a
 * publish mid-visit never mixes maps and content from two releases.
 *  - `?preview=prv_…` loads an authenticated draft preview (signed cookies, set by admin).
 *  - `?fixtures` in dev loads labelled local fixture data with no API.
 */
export async function loadRelease(): Promise<LoadResult> {
  const params = new URLSearchParams(location.search);
  try {
    const preview = params.get('preview');
    if (preview && Id.safeParse(preview).success) {
      const manifest = ReleaseManifest.parse(
        await getJson(`/preview/releases/${preview}/manifest.json`),
      );
      return { kind: 'release', manifest: { ...manifest, draft: true }, siteRevision: -1 };
    }
    if (import.meta.env.DEV && (params.has('fixtures') || import.meta.env.VITE_FIXTURES === '1')) {
      const { fixtureManifest } = await import('@ka/contracts/fixtures');
      return { kind: 'release', manifest: fixtureManifest(), siteRevision: 0 };
    }
    const site = PublicSite.parse(await getJson('/api/public/site'));
    if (!site.manifestUrl || !site.releaseId) return { kind: 'empty' };
    const manifest = ReleaseManifest.parse(await getJson(site.manifestUrl));
    return { kind: 'release', manifest, siteRevision: site.siteRevision };
  } catch (e) {
    console.error(e);
    return {
      kind: 'error',
      message: 'The site could not be reached. Please try again in a moment.',
    };
  }
}

/** True when a newer release has been activated since this page session started. */
export async function hasNewerRelease(currentRevision: number): Promise<boolean> {
  if (currentRevision < 0) return false;
  try {
    const site = PublicSite.parse(await getJson('/api/public/site'));
    return site.siteRevision > currentRevision;
  } catch {
    return false;
  }
}

export function assetUrl(m: ReleaseManifest, id: string | null | undefined): string | undefined {
  return id ? m.assets[id]?.url : undefined;
}

export const byNumberDesc = <T extends { number: number }>(a: T, b: T) => b.number - a.number;
